/**
 * <size-chart-loader>
 *
 * Lazily loads a size chart rendered by an alternate product template
 * (templates/product.<template>.json, fetched as `product.url?view=<template>`)
 * and injects its `[data-size-chart]` section into itself.
 *
 * Used inside the product size guide drawer (snippets/size-chart-drawer.liquid).
 * The element lives inside a <template> until the drawer opens, so the fetch
 * only happens on first open; the result is kept for subsequent opens.
 *
 * On failure the content of a child `<template data-size-chart-fallback>`
 * (the legacy size chart page) is shown, or `data-error-text` if there is none.
 *
 * Attributes:
 *   data-src         URL to fetch (required)
 *   data-error-text  Text shown when the fetch fails and no fallback exists
 */
(function () {
  if (customElements.get('size-chart-loader')) return;

  class SizeChartLoader extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this.load();
    }

    load() {
      const src = this.dataset.src;
      if (!src) {
        this.fail(new Error('size-chart-loader: missing data-src'));
        return;
      }

      this.setAttribute('data-state', 'loading');

      fetch(src, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`size-chart-loader: ${response.status} for ${src}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const selector = this.dataset.selector || '[data-size-chart]';
          const chart = doc.querySelector(selector);
          if (!chart) throw new Error(`size-chart-loader: no ${selector} in ${src}`);

          this.replaceChildren(document.adoptNode(chart));
          this.setAttribute('data-state', 'loaded');
        })
        .catch((error) => this.fail(error));
    }

    fail(error) {
      if (error) console.warn(error);

      const fallback = this.querySelector('template[data-size-chart-fallback]');

      if (fallback) {
        const content = document.createElement('div');
        content.className = 'size-chart-loader__fallback rte';
        content.appendChild(fallback.content.cloneNode(true));
        this.replaceChildren(content);
        this.setAttribute('data-state', 'fallback');
        return;
      }

      const message = document.createElement('p');
      message.className = 'size-chart-loader__error';
      message.textContent = this.dataset.errorText || 'Size chart unavailable.';
      this.replaceChildren(message);
      this.setAttribute('data-state', 'error');
    }
  }

  customElements.define('size-chart-loader', SizeChartLoader);
})();
